import { Link } from "react-router";
import { StackChart, type StackSeries } from "@/charts/StackChart";
import { EntityKind } from "@/generated/core/v1/financial_plan";
import { RetirementMethod } from "@/generated/core/v1/family";
import { ageOn, formatMonth } from "@/plan-logic/lib/dates";
import { ChartBox } from "@/components/ChartInfo";
import { type HeadCard, Headline } from "@/components/Headline";
import { money, moneyShort } from "@/plan-logic/lib/format";
import {
  balancesByKind,
  firstState,
  RETIREMENT_ACCOUNT_KINDS,
  retirementFunding,
  sumKinds,
  groupRrspMatches,
  groupRrspUuids,
  householdRetirement,
  type GroupRrspMatch,
  type IncomeSourceKey,
  type KindRow,
  retirementIncome,
} from "@/plan-logic/plan-view";
import { PlanPage, type PlanCtx } from "../PlanPage";

const LEVELS: Record<string, { title: string; what: string }> = {
  security: {
    title: "Financial security",
    what: "The bills that never stop. Your home, utilities, groceries, getting around, insurance, your children, and staying healthy.",
  },
  vitality: {
    title: "Financial vitality",
    what: "All of that, plus half of what you spend on eating out, clothes, subscriptions and the small things that make a week bearable.",
  },
  independence: {
    title: "Financial independence",
    what: "Your life exactly as you live it today, with nobody expecting you at work. Nothing given up.",
  },
};

const ACC_SERIES: StackSeries[] = [
  { k: String(EntityKind.NON_REGISTERED), n: "Non-registered", c: "var(--life)" },
  { k: String(EntityKind.RRSP), n: "RRSP", c: "var(--ess)" },
  {
    k: "groupRrsp",
    n: "Group RRSP (through work)",
    c: "color-mix(in srgb, var(--ess) 50%, var(--card))",
  },
  { k: String(EntityKind.DC_PENSION), n: "Workplace pension account", c: "var(--debt)" },
  { k: String(EntityKind.TFSA), n: "TFSA", c: "var(--save)" },
];
const DEC_SERIES: StackSeries[] = [
  { k: String(EntityKind.NON_REGISTERED), n: "Non-registered", c: "var(--life)" },
  { k: String(EntityKind.RRSP), n: "RRSP", c: "color-mix(in srgb, var(--ess) 60%, var(--card))" },
  { k: String(EntityKind.RRIF), n: "RRIF", c: "var(--ess)" },
  { k: String(EntityKind.DC_PENSION), n: "Workplace pension account", c: "var(--debt)" },
  { k: String(EntityKind.TFSA), n: "TFSA", c: "var(--save)" },
];
const INC_SERIES: { k: IncomeSourceKey; n: string; c: string }[] = [
  { k: "nonreg", n: "Non-registered", c: "var(--life)" },
  { k: "rrif", n: "RRIF", c: "var(--ess)" },
  { k: "cpp", n: "CPP", c: "var(--save)" },
  { k: "oas", n: "OAS", c: "var(--s5)" },
  { k: "dbPension", n: "DB pension", c: "var(--debt)" },
  { k: "dcPension", n: "Workplace pension", c: "color-mix(in srgb, var(--debt) 60%, var(--card))" },
  { k: "tfsa", n: "TFSA", c: "var(--s6)" },
  { k: "job", n: "Work", c: "var(--ink3)" },
  { k: "other", n: "Other income", c: "color-mix(in srgb, var(--ink3) 50%, var(--card))" },
];

const rowsOf = (rows: KindRow[], firstLabel?: string) =>
  rows.map((r, i) => ({
    label: i === 0 && firstLabel ? firstLabel : r.year,
    values: {
      ...Object.fromEntries(Object.entries(r.values).map(([k, v]) => [k, v ?? 0])),
      groupRrsp: r.groupRrsp,
    },
  }));

/** Keep only the series that have any value, so the legend lists what's there. */
function present(series: StackSeries[], rows: { values: Record<string, number> }[]) {
  return series.filter((s) => rows.some((r) => (r.values[s.k] ?? 0) > 0.5));
}

/** How much of the nest egg sits in group RRSPs at retirement, and how much of it the employer put in. */
function groupRrspLine(
  matches: GroupRrspMatch[],
  last: KindRow | undefined,
  year: number,
): string | undefined {
  if (!matches.length || !last?.groupRrsp || !last.total) return undefined;
  const one = matches.length === 1 ? matches[0] : undefined;
  const employer = one ? one.employer || "the employer" : "employers";
  const fromEmployer = matches.reduce(
    (s, m) => s + m.years.filter((y) => y.year <= year).reduce((t, y) => t + y.employer, 0),
    0,
  );
  const share = Math.round((last.groupRrsp / last.total) * 100);
  const holder = one
    ? `${one.firstName}’s group RRSP${one.employer ? ` at ${one.employer}` : ""} holds`
    : "your group RRSPs hold";
  return `By ${year}, ${holder} ${moneyShort(last.groupRrsp)}, ${share}% of what you’ve built. ${employer[0].toUpperCase()}${employer.slice(1)} put in ${money(fromEmployer)} of it as ${one ? "its" : "their"} match.`;
}

function Body({ ctx }: { ctx: PlanCtx }) {
  const { abi, naive, family } = ctx;
  const ret = householdRetirement(abi, family);
  const self = family.persons[0];
  if (!ret) {
    return (
      <div className="card pad" data-testid="no-retirement">
        <h2 style={{ marginBottom: 6 }}>Retirement isn&rsquo;t in this plan yet</h2>
        <p className="mut">
          Abi needs a retirement age to plan around. Set one on Update Data and your plan rebuilds.
        </p>
      </div>
    );
  }
  const d = ret.details;
  const year = ret.date.year;
  const age = ageOn(self?.dob, ret.date) ?? d.retirementAge;
  const reach = d.projectedNestEgg >= d.nestEggRequired;
  const target = family.retirementMethod === RetirementMethod.TARGET;
  const goal = self?.retirementGoal;

  const accRows = balancesByKind(abi, ret.date, "before", undefined, groupRrspUuids(family, abi));
  const acc = rowsOf(accRows, "Now");
  const groupLine = groupRrspLine(groupRrspMatches(abi, family, ctx.today), accRows.at(-1), year);
  const dec = rowsOf(balancesByKind(abi, ret.date, "after"));
  const inc = retirementIncome(abi, ret.date).map((r) => ({
    label: r.year,
    values: Object.fromEntries(Object.entries(r.values).map(([k, v]) => [k, v ?? 0])),
  }));
  const abiF = retirementFunding(abi, family);
  const naiveF = naive ? retirementFunding(naive, family) : undefined;
  // "Funded today" (v45): what the retirement accounts hold today against the nest egg the engine
  // says it takes, the same kind of money the engine's projected nest egg counts.
  const held = sumKinds(firstState(abi), RETIREMENT_ACCOUNT_KINDS);
  const pc = (f: number) => `${Math.round(Math.max(0, Math.min(1, f)) * 100)}%`;
  const head: HeadCard[] = [
    {
      k: "Funded today",
      v: pc(d.nestEggRequired > 0 ? held / d.nestEggRequired : 0),
      s: `${money(held)} in your retirement accounts against the ${money(d.nestEggRequired)} it takes to stop in ${year}`,
    },
  ];
  if (naiveF && abiF) {
    const gain = Math.round((Math.min(1, abiF.completion) - Math.min(1, naiveF.completion)) * 100);
    head.push(
      {
        k: "On your current plan",
        v: `${pc(naiveF.completion)} funded`,
        s: `by ${year}, if nothing changes`,
      },
      {
        k: "With Abi’s plan",
        abi: true,
        from: pc(naiveF.completion),
        to: `${pc(abiF.completion)} funded`,
        lead: `${gain >= 0 ? "+" : "−"}${Math.abs(gain)}% funded`,
        leadTone: gain > 0 ? "ok" : gain < 0 ? "debt" : "flat",
        s: `by ${year}, the year you chose to stop`,
        testId: "hd-abi",
      },
    );
  } else if (abiF) {
    head.push({
      k: "With Abi’s plan",
      abi: true,
      v: `${pc(abiF.completion)} funded`,
      s: `by ${year}`,
    });
  }

  return (
    <>
      <Headline id="hd-ret" cards={head} />
      {target && (
        <div
          className="card pad"
          id="ret-pick"
          style={{ marginBottom: 16 }}
          data-testid="ret-target"
        >
          <h2 style={{ marginBottom: 4 }}>Your target</h2>
          <p className="mut" style={{ marginBottom: 18, maxWidth: "60ch" }}>
            {goal?.incomeReplacementPercent
              ? `${goal.incomeReplacementPercent}% of today's income`
              : "The target you set"}
            {goal?.retirementAge ? `, stopping at ${goal.retirementAge}` : ""}. Change it on{" "}
            <Link className="wp-link" to="/app/update-data">
              Update Data
            </Link>
            .
          </p>
          <div className="retcmp">
            <div>
              <div className="eyebrow">You&rsquo;d need</div>
              <div className="ffbig" style={{ fontSize: 34 }}>
                {moneyShort(d.nestEggRequired)}
              </div>
              <p className="mut">
                {money(d.targetAnnualSpending)} a year to spend, in today&rsquo;s dollars.
              </p>
            </div>
            <div>
              <div className="eyebrow">By {year} you&rsquo;d have</div>
              <div
                className="ffbig"
                style={{ fontSize: 34, color: reach ? "var(--ok)" : "var(--warn)" }}
              >
                {moneyShort(d.projectedNestEgg)}
              </div>
              <p className="mut">
                {reach
                  ? "Comfortably past it."
                  : `Short by ${money(d.nestEggRequired - d.projectedNestEgg)}. Either work longer, save more, or spend less in retirement.`}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div id="ff-levels" data-testid="ret-levels">
          {d.levels.length === 0 && (
            <p className="mut">
              Abi works out the levels from your spending. They&rsquo;ll appear here once your plan
              includes them.
            </p>
          )}
          {d.levels.map((l, i) => {
            const meta = LEVELS[l.name] ?? { title: l.name, what: "" };
            const pctv =
              l.nestEggRequired > 0
                ? Math.min(100, (d.projectedNestEgg / l.nestEggRequired) * 100)
                : 100;
            return (
              <div className="fflvl" key={l.name}>
                <div className="fflhead">
                  <span className="n">{i + 1}</span>
                  <div style={{ flex: 1 }}>
                    <b>{meta.title}</b>
                    <p>{meta.what}</p>
                  </div>
                  <div className="ffegg">
                    <div className="tnum">{moneyShort(l.nestEggRequired)}</div>
                    <span>the nest egg</span>
                  </div>
                </div>
                <div className="ffrow">
                  <span>{money(l.annualSpending / 12)} a month</span>
                  <span>{money(l.annualSpending)} a year, after tax, in today&rsquo;s dollars</span>
                </div>
                <div className="acctrack">
                  <span style={{ width: `${pctv}%` }} />
                </div>
                <div className="ffpct">
                  <span>
                    {pctv >= 100 ? "Reached" : `${pctv.toFixed(1)}% of the way there`} by {year}, on
                    the plan
                  </span>
                  <span className="mut">
                    {pctv >= 100 ? "" : `${money(l.nestEggRequired - d.projectedNestEgg)} to go`}
                  </span>
                </div>
              </div>
            );
          })}
          {d.levels.length > 0 && (
            <p className="hint">
              Each nest egg assumes you stop in {formatMonth(ret.date)}
              {age ? `, at ${age}` : ""}. Progress is what your plan has built by then:{" "}
              {money(d.projectedNestEgg)}.
            </p>
          )}
        </div>
      </div>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 4 }}>Building it up</h2>
        <p className="mut" style={{ marginBottom: 16, maxWidth: "66ch" }}>
          What&rsquo;s in each account on the way to {year}, and which one is doing the work.
        </p>
        <ChartBox id="ff-acc">
          <StackChart
            rows={acc}
            series={present(ACC_SERIES, acc)}
            label={`Retirement savings building up to ${year}, by account`}
            note="FHSA is not here. It is a first-home account, not a retirement one. Anything left in it rolls into your RRSP, and that transfer shows up in the RRSP bar."
            testId="ret-acc"
          />
        </ChartBox>
        {groupLine && (
          <p
            className="mut"
            style={{ marginTop: 12, maxWidth: "66ch" }}
            data-testid="ret-group-rrsp"
          >
            {groupLine}
          </p>
        )}
      </div>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 4 }}>Drawing it down</h2>
        <p className="mut" style={{ marginBottom: 16, maxWidth: "66ch" }}>
          What&rsquo;s left in each account through retirement. Order matters, and drawing from the
          wrong one first can cost years.
        </p>
        <ChartBox id="ff-dec">
          <StackChart
            rows={dec}
            series={present(DEC_SERIES, dec)}
            label={`Retirement account balances drawing down from ${year}`}
            testId="ret-dec"
          />
        </ChartBox>
      </div>

      <div className="card pad">
        <h2 style={{ marginBottom: 4 }}>Where each year&rsquo;s income comes from</h2>
        <p className="mut" style={{ marginBottom: 16, maxWidth: "66ch" }}>
          The same years again, but as income rather than balances. This is the one that answers
          &ldquo;what will actually land in my account.&rdquo;
        </p>
        <ChartBox id="ff-income">
          <StackChart
            rows={inc}
            series={present(INC_SERIES, inc)}
            label={`Where each year of retirement income comes from, from ${year}`}
            note="Before tax. Government benefits, pensions and withdrawals as the plan draws them."
            testId="ret-income"
            colLabel="All sources"
          />
        </ChartBox>
      </div>
    </>
  );
}

export function RetirementPage() {
  return (
    <PlanPage title="pages.retirement.title" sub="pages.retirement.sub" testId="retirement">
      {(ctx) => <Body ctx={ctx} />}
    </PlanPage>
  );
}
